import { type Page, toListQuery } from '@hello-aria/makalu-api-client'
import { LogicalFilter, QueryStringInput } from '@hello-aria/query-string'
import { Company } from '../../../shared/models/companyModel'
import {
    CreateCreditFields,
    CreateCreditGateway,
    Credit,
    toCredit,
    RegisterCreditLimitBody,
    UpdateCredit,
    UpdateCreditGateway
} from '../../../shared/models/creditModels'
import { AttachmentWithIdsGateway, requireFileId } from '../../../shared/models/uploadModels'
import { makaluApi } from '../../../shared/utils/makaluApiClient'
import { uploadFiles } from '../../../shared/services/uploadServices'
import { requirePathParam } from '../../../shared/utils/requestsUtils'
import { isNil } from 'lodash'
import { removeWhiteSpace } from '../../../shared/utils/stringUtils'
import { Identifier } from '@/shared/models/debtorModels'
import { trackEvent } from '../../../shared/utils/analyticsUtils'
import { handleHttpError } from '@/shared/utils/queryUtils'

interface GetCredits {
    companyId?: Company['id']
    query?: QueryStringInput
    signal?: AbortSignal
}

export const listCredits = async (options?: GetCredits): Promise<Page<Credit>> => {
    try {
        const { query, companyId } = options || {}
        const result = await makaluApi.listPage('/companies/{companyId}/credit-limits', {
            params: {
                path: { companyId: requirePathParam('companyId', companyId) },
                query: toListQuery({
                    ...query,
                    sorts: query?.sorts ?? [
                        {
                            path: 'updatedAt',
                            direction: 'desc'
                        }
                    ]
                })
            },
            signal: options?.signal
        })

        return { ...result, body: result.body.map(toCredit) }
    } catch (error) {
        return handleHttpError(listCredits.name, error)
    }
}

export const getCredit = async (
    companyId: Company['id'],
    creditId: Credit['id'] | undefined | null
): Promise<Credit | undefined> => {
    try {
        if (isNil(creditId)) {
            throw new Error('Credit ID is required')
        }

        const response = await makaluApi.GET('/companies/{companyId}/credit-limits/{creditLimitId}', {
            params: { path: { companyId, creditLimitId: creditId } }
        })

        const credit = toCredit(response)
        return {
            ...credit,
            allocations: [...credit.allocations].sort((a, b) =>
                a.guarantor === 'aria' ? -1 : b.guarantor === 'aria' ? 1 : 0
            )
        }
    } catch (error) {
        return handleHttpError(getCredit.name, error)
    }
}

export const registerCredit = async (
    payload: CreateCreditFields,
    companyId: Company['id']
): Promise<Credit | undefined> => {
    const sanitizedPayload: RegisterCreditLimitBody = {
        claimedAmount: payload.claimedAmount,
        claimedCurrency: payload.claimedCurrency,
        debtorIdentifier: payload.debtorIdentifier,
        debtorId: payload.debtorId ?? undefined
    }

    const response = await makaluApi.POST('/companies/{companyId}/credit-limits', {
        params: { path: { companyId } },
        body: sanitizedPayload
    })

    trackEvent('Create_CreditLimit')

    return toCredit(response)
}

export const createCredit = async (
    payload: CreateCreditGateway,
    companyId: Company['id']
): Promise<Credit | undefined> => {
    const sanitizedPayload: CreateCreditGateway = {
        claimedAmount: payload.claimedAmount,
        claimedCurrency: payload.claimedCurrency,
        debtorIdentifier: payload.debtorIdentifier,
        debtorId: payload.debtorId,
        guarantor: payload.guarantor,
        ...(payload.attachments?.length && { attachments: payload.attachments })
    }

    const response = await makaluApi.POST('/companies/{companyId}/credit-limits', {
        params: { path: { companyId } },
        body: sanitizedPayload
    })

    trackEvent('Create_CreditLimit')

    return toCredit(response)
}

export const updateCredit = async (payload: UpdateCredit, companyId: Company['id']): Promise<Credit | undefined> => {
    const attachments = payload.attachments ? await uploadFiles(payload.attachments, companyId) : null
    const attachmentIds: AttachmentWithIdsGateway[] =
        attachments?.map((attachment) => ({ id: requireFileId(attachment) })) || []

    const sanitizedPayload: UpdateCreditGateway = {
        claimedAmount: payload.claimedAmount,
        claimedCurrency: payload.claimedCurrency,
        debtorIdentifier: payload.debtorIdentifier,
        debtorId: payload.debtorId ?? undefined,
        comment: payload.comment || null,
        attachments: attachmentIds,
        guarantor: payload.guarantor
    }

    const response = await makaluApi.POST('/companies/{companyId}/credit-limits', {
        params: { path: { companyId } },
        body: sanitizedPayload
    })

    trackEvent('Update_CreditLimit')

    return toCredit(response)
}

export const listCreditLimitsByDebtorIdentifier = async (params: {
    companyId: Company['id']
    debtorIdentifier: Identifier | undefined
}): Promise<Page<Credit>> => {
    const { companyId, debtorIdentifier } = params
    const filters: QueryStringInput['filters'] = []
    if (!isNil(debtorIdentifier)) {
        const additionalFilter: LogicalFilter = {
            type: 'logical',
            operator: 'and',
            filters: [
                {
                    path: 'debtorIdentifier.value',
                    type: 'comparison',
                    value: removeWhiteSpace(debtorIdentifier.value),
                    operator: 'eq'
                },
                {
                    path: 'debtorIdentifier.type',
                    type: 'comparison',
                    value: debtorIdentifier.type,
                    operator: 'eq'
                }
            ]
        }
        filters.push(additionalFilter)
    }

    if (filters.length === 0) {
        return { body: [], pagination: {} }
    }

    const response = await makaluApi.listPage('/companies/{companyId}/credit-limits', {
        params: { path: { companyId }, query: toListQuery({ filters }) }
    })

    return { ...response, body: response.body.map(toCredit) }
}
